/**
 * Movimientos Territoriales Report
 * Minister transfers between districts/regions with flow summary.
 */

import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AnimatedCard } from "@/components/ui/animated-card";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { DateRangeFilter } from "@/components/atoms/filters/DateRangeFilter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataColumnHeader } from "@/components/molecules/table";
import {
  SelfContainedDataTable,
  selectedIncludesFn,
} from "@/components/organisms/SelfContainedDataTable";
import { BarChartAnimated } from "@/components/ui/bar-chart-animated";
import { useMinisterTransfersReport } from "../hooks/useMinisterTransfersReport";
import { ExportDropdown } from "../components/ExportDropdown";
import type { ExportColumn } from "../utils/exportExcel";
import type { MinisterTransferEntry, TransferDistrictFlow } from "../types";

const columns: ColumnDef<MinisterTransferEntry>[] = [
  {
    accessorKey: "adb_code",
    header: ({ column }) => <DataColumnHeader column={column} title="Código ADB" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => <span className="font-mono text-xs">{row.original.adb_code ?? "—"}</span>,
  },
  {
    accessorKey: "minister_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Ministro" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => <span className="font-medium">{row.original.minister_name}</span>,
  },
  {
    accessorKey: "district_from_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Distrito origen" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.district_from_name ?? "—",
  },
  {
    accessorKey: "region_from_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Región origen" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.region_from_name ?? "—",
  },
  {
    id: "arrow",
    header: "",
    enableSorting: false,
    enableColumnFilter: false,
    cell: () => <ArrowRight className="size-3 text-muted-foreground" />,
  },
  {
    accessorKey: "district_to_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Distrito destino" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.district_to_name ?? "—",
  },
  {
    accessorKey: "region_to_name",
    header: ({ column }) => <DataColumnHeader column={column} title="Región destino" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => row.original.region_to_name ?? "—",
  },
  {
    accessorKey: "event_date",
    header: ({ column }) => <DataColumnHeader column={column} title="Fecha" />,
    filterFn: selectedIncludesFn,
    cell: ({ row }) => new Date(row.original.event_date).toLocaleDateString("es-BO"),
  },
  {
    accessorKey: "notes",
    header: "Notas",
    enableSorting: false,
    enableColumnFilter: false,
    cell: ({ row }) => (
      <span className="block max-w-[180px] truncate text-muted-foreground" title={row.original.notes ?? undefined}>
        {row.original.notes ?? "—"}
      </span>
    ),
  },
];

const EXPORT_COLUMNS: ExportColumn<MinisterTransferEntry>[] = [
  { label: "Código ADB",       getValue: (r) => r.adb_code ?? "" },
  { label: "Ministro",         getValue: (r) => r.minister_name },
  { label: "Distrito Origen",  getValue: (r) => r.district_from_name ?? "" },
  { label: "Región Origen",    getValue: (r) => r.region_from_name ?? "" },
  { label: "Distrito Destino", getValue: (r) => r.district_to_name ?? "" },
  { label: "Región Destino",   getValue: (r) => r.region_to_name ?? "" },
  { label: "Distrito Actual",  getValue: (r) => r.current_district_name ?? "" },
  { label: "Región Actual",    getValue: (r) => r.current_region_name ?? "" },
  { label: "Fecha",            getValue: (r) => new Date(r.event_date).toLocaleDateString("es-BO") },
  { label: "Notas",            getValue: (r) => r.notes ?? "" },
];

export function MinisterTransfersReportPage() {
  const navigate = useNavigate();

  const { transfers, districtFlow, filters, isLoading, error, updateFilter, resetFilters } =
    useMinisterTransfersReport();

  const [filteredRows, setFilteredRows] = useState<MinisterTransferEntry[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);

  const handleFilteredDataChange = useCallback(
    (rows: MinisterTransferEntry[], filtered: boolean) => {
      setFilteredRows(rows);
      setIsFiltered(filtered);
    },
    []
  );

  const filterPanel = (
    <div className="flex flex-wrap items-end gap-3 p-3">
      <DateRangeFilter
        dateFrom={filters.dateFrom}
        dateTo={filters.dateTo}
        onDateFromChange={(v) => updateFilter("dateFrom", v)}
        onDateToChange={(v) => updateFilter("dateTo", v)}
        label="Período"
      />
      <Button variant="outline" size="sm" onClick={resetFilters}>Limpiar</Button>
    </div>
  );

  if (error) {
    return (
      <div className="space-y-4 p-6">
        <Button variant="ghost" onClick={() => navigate("/reports")}>
          <ArrowLeft className="mr-2 size-4" />Volver a Reportes
        </Button>
        <div className="flex h-64 items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5">
          <p className="text-destructive">Error al cargar el reporte</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div>
        <Button variant="ghost" onClick={() => navigate("/reports")} className="mb-4">
          <ArrowLeft className="mr-2 size-4" />Volver a Reportes
        </Button>
        <h1 className="text-3xl font-bold">Movimientos Territoriales</h1>
        <p className="mt-2 text-muted-foreground">
          Traslados de ministros entre distritos y regiones
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <AnimatedCard variant="gradient" className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Total traslados</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="h-8 w-16 animate-pulse rounded bg-muted" /> : (
              <AnimatedNumber value={transfers.length} className="text-2xl font-bold md:text-3xl" />
            )}
          </CardContent>
        </AnimatedCard>
        <AnimatedCard variant="gradient" className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Distritos involucrados</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? <div className="h-8 w-16 animate-pulse rounded bg-muted" /> : (
              <AnimatedNumber value={districtFlow.length} className="text-2xl font-bold md:text-3xl" />
            )}
          </CardContent>
        </AnimatedCard>
        <AnimatedCard variant="gradient" className="border-0 col-span-2 sm:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-muted-foreground">Período</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm font-medium">{filters.dateFrom} → {filters.dateTo}</p>
          </CardContent>
        </AnimatedCard>
      </div>

      {/* Net flow bar chart */}
      {!isLoading && districtFlow.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Flujo Neto por Distrito</CardTitle>
          </CardHeader>
          <CardContent>
            <BarChartAnimated
              data={districtFlow.map((row) => ({
                name: row.district_name,
                value: row.net,
                fill: row.net > 0 ? "#22c55e" : row.net < 0 ? "#ef4444" : "#94a3b8",
              }))}
              height={260}
            />
          </CardContent>
        </Card>
      )}

      {/* District flow summary */}
      {!isLoading && districtFlow.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Flujo por Distrito</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Distrito</TableHead>
                  <TableHead className="w-[100px] text-center text-success">Entradas</TableHead>
                  <TableHead className="w-[100px] text-center text-destructive">Salidas</TableHead>
                  <TableHead className="w-[100px] text-center">Neto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {districtFlow.map((row: TransferDistrictFlow) => (
                  <TableRow key={row.district_id}>
                    <TableCell className="font-medium">{row.district_name}</TableCell>
                    <TableCell className="text-center tabular-nums text-success">+{row.incoming}</TableCell>
                    <TableCell className="text-center tabular-nums text-destructive">-{row.outgoing}</TableCell>
                    <TableCell className={`text-center tabular-nums font-semibold ${row.net > 0 ? "text-success" : row.net < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                      {row.net > 0 ? `+${row.net}` : row.net}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Transfers table */}
      <SelfContainedDataTable
        data={transfers}
        columns={columns}
        isLoading={isLoading}
        getSearchableText={(row) => {
          const adbCode = row.adb_code ?? "";
          return `${adbCode} ${adbCode.replace(/\D/g, "")} ${row.minister_name ?? ""}`;
        }}
        searchPlaceholder="Buscar por nombre o código ADB..."
        initialSorting={[{ id: "minister_name", desc: false }]}
        filterPanel={filterPanel}
        emptyMessage="No se encontraron traslados para el período seleccionado"
        defaultPageSize={50}
        pageSizeOptions={[20, 50, 100]}
        onFilteredDataChange={handleFilteredDataChange}
        toolbarActions={
          <ExportDropdown
            rows={filteredRows}
            isFiltered={isFiltered}
            filename={`transferencias-${filters.dateFrom}_${filters.dateTo}`}
            columns={EXPORT_COLUMNS}
            getDistrictName={(r) => r.district_to_name ?? null}
            getRegionName={(r) => r.region_to_name ?? null}
            getSortName={(r) => r.minister_name ?? ""}
            disabled={isLoading}
          />
        }
      />
    </div>
  );
}
